import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { parseOAuthCallbackError, clearOAuthErrorFromUrl, mapOAuthErrorToMessage } from '../utils/authErrors';
import { UserProfile } from '../types';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
  sendPasswordResetEmail: (email: string, redirectTo: string) => Promise<{ error: AuthError | null }>;
  signInWithOAuth: (provider: 'google', redirectTo?: string) => Promise<{ error: AuthError | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  deleteAccount: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from database
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return data as UserProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchUserProfile(session.user.id).then(setProfile);
      }

      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          const userProfile = await fetchUserProfile(session.user.id);
          setProfile(userProfile);
        } else {
          setProfile(null);
        }

        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Handle OAuth callback errors from URL fragment
  useEffect(() => {
    const oauthError = parseOAuthCallbackError();

    if (oauthError) {
      console.error('OAuth callback error:', oauthError);

      // Store raw error details for AuthModal to localize and display
      sessionStorage.setItem('oauth_error', JSON.stringify(oauthError));

      // Clear error from URL
      clearOAuthErrorFromUrl();
    }
  }, []);

  // Sign in with email and password
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    return { error };
  }, []);

  // Sign up with email and password
  const signUpWithEmail = useCallback(async (
    email: string,
    password: string,
    displayName?: string
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName || email.split('@')[0]
        }
      }
    });

    return { error };
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    const clearAllAuthData = () => {
      // Clear local state
      setUser(null);
      setProfile(null);
      setSession(null);

      // Clear all Supabase-related data from localStorage and sessionStorage
      const clearStorage = (storage: Storage) => {
        const keys = Object.keys(storage);
        const supabaseKeys = keys.filter(key => key.includes('supabase') || key.startsWith('sb-'));
        supabaseKeys.forEach(key => {
          storage.removeItem(key);
        });
      };

      clearStorage(localStorage);
      clearStorage(sessionStorage);
    };

    try {
      const timeoutPromise = new Promise<{ error: null }>((resolve) => {
        setTimeout(() => {
          resolve({ error: null });
        }, 2000);
      });

      const signOutPromise = supabase.auth.signOut();

      const result = await Promise.race([signOutPromise, timeoutPromise]);

      if (result.error) {
        console.error('AuthContext: Supabase signOut error:', result.error);
      }

      // Always clear everything regardless of Supabase response
      clearAllAuthData();
    } catch (error) {
      console.error('AuthContext: Exception in signOut:', error);

      // Force clear everything even on error
      clearAllAuthData();
    }
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        return { error };
      }

      // Refresh profile data
      const updatedProfile = await fetchUserProfile(user.id);
      setProfile(updatedProfile);

      return { error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error: error as Error };
    }
  }, [user]);

  // Send password reset email
  const sendPasswordResetEmail = useCallback(async (email: string, redirectTo: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    return { error };
  }, []);

  // Sign in with OAuth provider
  const signInWithOAuth = useCallback(async (
    provider: 'google',
    redirectTo?: string
  ): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectTo || `${window.location.origin}/app`,
        },
      });

      return { error };
    } catch (err) {
      console.error('Unexpected OAuth error:', err);
      return {
        error: {
          message: 'OAuth Error',
          name: 'OAuthError',
          status: 500,
        } as AuthError,
      };
    }
  }, []);

  // Update user email
  const updateEmail = useCallback(async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    return { error };
  }, []);

  // Update user password
  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }, []);

  // Delete user account
  const deleteAccount = useCallback(async () => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      // Delete user profile first
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) {
        console.error('Error deleting user profile:', profileError);
        return { error: profileError };
      }

      // Delete auth user (this will cascade delete related data due to RLS)
      const { error: authError } = await supabase.rpc('delete_user');

      if (authError) {
        console.error('Error deleting auth user:', authError);
        return { error: authError };
      }

      // Sign out after deletion
      await signOut();

      return { error: null };
    } catch (error) {
      console.error('Error deleting account:', error);
      return { error: error as Error };
    }
  }, [user, signOut]);

  const value = useMemo(
    () => ({
      user,
      profile,
      session,
      loading,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      updateProfile,
      sendPasswordResetEmail,
      signInWithOAuth,
      updateEmail,
      updatePassword,
      deleteAccount,
    }),
    [
      user,
      profile,
      session,
      loading,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      updateProfile,
      sendPasswordResetEmail,
      signInWithOAuth,
      updateEmail,
      updatePassword,
      deleteAccount,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
